import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from '@billda/design-system';
import { useDebugStore } from '../../stores/debugStore';
import { JwtDebugSection } from './JwtDebugSection';
import { UserDataSection } from './UserDataSection';
import { TimeDebugSection } from './TimeDebugSection';
import { JobsDebugSection } from './JobsDebugSection';
import { PotsDebugSection } from './PotsDebugSection';
import { FeatureSwitchGate } from '../gates/FeatureSwitchGate';
import { Features } from '@billda/features-ui';

const DEBUG_TABS = [
  { value: 'jwt', label: 'JWT Token' },
  { value: 'user', label: 'User Data' },
  { value: 'time', label: 'Time' },
  { value: 'jobs', label: 'Jobs' },
  { value: 'pots', label: 'Pots' },
] as const;

export const DebugPanel: React.FC = () => {
  const { isDebugOpen, closeDebug, toggleDebug } = useDebugStore();
  const [tabValue, setTabValue] = React.useState<string>('jwt');

  const handleClose = () => {
    closeDebug();
  };

  // Keyboard shortcut: Ctrl+Shift+D (or Cmd+Shift+D on Mac)
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl (Windows/Linux) or Cmd (Mac) + Shift + D
      const isCtrlOrCmd = event.ctrlKey || event.metaKey;

      if (
        isCtrlOrCmd &&
        event.shiftKey &&
        (event.key === 'd' || event.key === 'D')
      ) {
        event.preventDefault();
        toggleDebug();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleDebug]);

  return (
    <Dialog
      open={isDebugOpen}
      onOpenChange={next => {
        if (!next) {
          handleClose();
        }
      }}
    >
      <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-5xl overflow-hidden p-0">
        <DialogHeader className="flex items-center gap-2 border-b border-border-default px-4 pb-3 pr-12">
          <Text as="h6" variant="h6">
            🐛 Debug Panel
          </Text>
          <Text variant="caption" color="secondary">
            Development Tools
          </Text>
        </DialogHeader>

        <Tabs
          value={tabValue}
          onValueChange={setTabValue}
          className="flex min-h-0 flex-col"
        >
          <div className="border-b border-border-default px-2">
            <TabsList variant="line" aria-label="debug panel tabs">
              {DEBUG_TABS.map(tab => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <div className="max-h-[calc(85vh-10rem)] overflow-auto p-4">
            <TabsContent value="jwt" className="mt-0">
              <JwtDebugSection />
            </TabsContent>
            <TabsContent value="user" className="mt-0">
              <UserDataSection />
            </TabsContent>
            <TabsContent value="time" className="mt-0">
              <FeatureSwitchGate
                feature={Features.Debug.IsTimeManipulationEnabled}
                showDisabledMessage
              >
                <TimeDebugSection />
              </FeatureSwitchGate>
            </TabsContent>
            <TabsContent value="jobs" className="mt-0">
              <JobsDebugSection />
            </TabsContent>
            <TabsContent value="pots" className="mt-0">
              <FeatureSwitchGate
                feature={Features.Debug.IsTimeManipulationEnabled}
                showDisabledMessage
              >
                <PotsDebugSection />
              </FeatureSwitchGate>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
